import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import creators from '../data/creators.json';
import { THEMES, DEFAULT_THEME } from '../utils/oshiThemes';
import OshiCreatorCard from '../components/OshiCreatorCard';
import '../styles/OshiTop3.css';

const GROUPS = ['すとぷり', '騎士X', 'AMPTAK×COLORS', 'めておら', 'すにすて', 'とぅるりぷ'];

function getGroupTheme(creatorId) {
  const creator = creators.find((c) => c.id === creatorId);
  return creator ? (THEMES[creator.group] || DEFAULT_THEME) : DEFAULT_THEME;
}

const OshiTop3Page = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const navigate = useNavigate();

  const activeTheme = useMemo(() => {
    if (selectedIds.length === 0) return DEFAULT_THEME;
    return getGroupTheme(selectedIds[0]);
  }, [selectedIds]);

  const buttonGradient = useMemo(() => {
    const colors = selectedIds.map((id) => getGroupTheme(id).accent);
    if (colors.length === 3) {
      return `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`;
    }
    return activeTheme.buttonGradient;
  }, [selectedIds, activeTheme]);

  const buttonGlow = useMemo(
    () => (selectedIds[0] ? getGroupTheme(selectedIds[0]).accentGlow : activeTheme.accentGlow),
    [selectedIds, activeTheme]
  );

  const selectedCreators = selectedIds
    .map((id) => creators.find((c) => c.id === id))
    .filter(Boolean);

  function toggleCreator(id) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  }

  useEffect(() => {
    if (selectedIds.length === 3) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [selectedIds.length]);

  function handleShare() {
    navigate(`/oshi3/share?ids=${selectedIds.join(',')}`);
  }

  return (
    <div className="oshi3-page">
      {/* 背景レイヤー */}
      {selectedCreators.length > 0 && (
        <div className="oshi3-bg-panels">
          {selectedCreators.map((creator, i) => {
            const theme = THEMES[creator.group] || DEFAULT_THEME;
            return (
              <div key={creator.id} className="oshi3-bg-panel">
                <img src={creator.image} alt="" aria-hidden="true" />
                <div className="oshi3-bg-panel-overlay" style={{ background: theme.panelOverlay }} />
                {i > 0 && (
                  <div
                    className="oshi3-bg-panel-separator"
                    style={{
                      background: `linear-gradient(90deg, transparent, ${theme.accent}80, transparent)`,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* UIオーバーレイ：未選択時は黒基調、選択後はテーマ色 */}
      <div
        className="oshi3-ui-overlay"
        style={{
          background: selectedCreators.length === 0
            ? 'rgba(13, 13, 13, 0.85)'
            : activeTheme.uiOverlay
        }}
      />

      {/* コンテンツ */}
      <main className="oshi3-content">
        <header className="oshi3-header">
          <h1 className="oshi3-title">
            推しメン3
          </h1>
          <p className="oshi3-subtitle" style={{ color: selectedCreators.length === 0 ? '#aaaaaa' : activeTheme.subtitleColor }}>
            STPRクリエイターから推しを3人選んでシェアしよう
          </p>
        </header>

        {GROUPS.map((group) => {
          const groupCreators = creators.filter((c) => c.group === group);
          const groupTheme = THEMES[group] || DEFAULT_THEME;
          return (
            <div key={group} className="oshi3-group">
              <div className="oshi3-group-label-row">
                {groupTheme.logo && (
                  <img
                    src={groupTheme.logo}
                    alt={group}
                    className="oshi3-group-logo"
                  />
                )}
                <h2
                  className="oshi3-group-label"
                  style={{
                    color: selectedCreators.length === 0 ? '#cccccc' : activeTheme.groupLabelColor,
                    textShadow: selectedCreators.length === 0 ? 'none' : activeTheme.groupLabelGlow,
                  }}
                >
                  {group}
                </h2>
              </div>
              <div className="oshi3-creator-grid">
                {groupCreators.map((creator) => {
                  const rank = selectedIds.indexOf(creator.id);
                  return (
                    <OshiCreatorCard
                      key={creator.id}
                      creator={creator}
                      selected={selectedIds.includes(creator.id)}
                      rank={rank !== -1 ? rank + 1 : null}
                      onClick={() => toggleCreator(creator.id)}
                      theme={activeTheme}
                      ownAccent={groupTheme.accent}
                      ownAccentGlow={groupTheme.accentGlow}
                      ownCardTint={groupTheme.cardTint}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </main>

      {/* 固定フッター */}
      <div className="oshi3-sticky-footer">
        <p className="oshi3-count-text" style={{ color: selectedCreators.length === 0 ? '#888888' : activeTheme.countColor }}>
          {selectedIds.length}/3 人選択中
        </p>
        <button
          className="oshi3-share-btn"
          onClick={handleShare}
          disabled={selectedIds.length !== 3}
          style={{
            background: buttonGradient,
            boxShadow: `0 4px 24px ${buttonGlow}`,
          }}
        >
          シェアする →
        </button>
      </div>
    </div>
  );
};

export default OshiTop3Page;
